import type { FunctionComponent, VoidFunctionComponent } from 'react';
import Badge from '../Badge/Badge';
import Tooltip, { TooltipContent } from '../Tooltip';
import { severityToBadgeColor } from './utils';
import { VulnSeverity } from '../../../../shared/issuesV2';

type Props = {
  //  The CVSS score
  cvss: number | null;
  //  If the label for the badge should be shortened (ie: "CRIT" instead of "Critical") (@default true)
  isTruncated?: boolean;
  // If enabled, display a tooltip above the badge.
  hasTooltip?: boolean;
};

type TooltipWrapperProps = {
  label: string;
  cvss: number | null;
  id: string;
  hasTooltip: boolean;
};

const TooltipWrapper: FunctionComponent<TooltipWrapperProps> = (
  {
    children,
    label,
    cvss,
    id,
    hasTooltip,
  }) => {
  return (
    <Tooltip
      showTooltip={hasTooltip}
      id={id}
      tooltip={
        <TooltipContent>
          <div>{label}</div>
          <div>Score: {cvss === null ? 'unknown' : cvss}</div>
        </TooltipContent>
      }
    >
      <div>{children}</div>
    </Tooltip>
  );
};

/**
 * Displays a color badge base on the cvss.
 *
 *  @example
 *  import VulnBadge from '../generic/vulnBadge';
 *  ...
 *  <VulnBadge cvss={9}/> // Critical severity badge
 *  <VulnBadge cvss={7}/> // High severity badge
 *  <VulnBadge cvss={5}/> // Medium severity badge
 *  <VulnBadge cvss={3}/> // Low severity badge
 *  <VulnBadge /> // Unknown severity badge
 */
const VulnBadge: VoidFunctionComponent<Props> = ({ cvss, isTruncated = true, hasTooltip = true }) => {
  if (cvss) {
    if (cvss >= 9 && cvss <= 10) {
      const label = isTruncated ? 'CRIT' : 'Critical';
      return (
        <TooltipWrapper
          id="critical severity"
          label="Critical severity vulnerability"
          cvss={cvss}
          hasTooltip={hasTooltip}
        >
          <Badge color={severityToBadgeColor(VulnSeverity.Critical)}>
            {label} {cvss}
          </Badge>
        </TooltipWrapper>
      );
    }
    if (cvss >= 7 && cvss < 9) {
      const label = isTruncated ? 'HIGH' : 'High';
      return (
        <TooltipWrapper
          id="high severity"
          label="High severity vulnerability"
          cvss={cvss}
          hasTooltip={hasTooltip}
        >
          <Badge color={severityToBadgeColor(VulnSeverity.High)}>
            {label} {cvss}
          </Badge>
        </TooltipWrapper>
      );
    }
    if (cvss >= 4 && cvss < 7) {
      const label = isTruncated ? 'MED' : 'Medium';
      return (
        <TooltipWrapper
          id="medium severity"
          label="Medium severity vulnerability"
          cvss={cvss}
          hasTooltip={hasTooltip}
        >
          <Badge color={severityToBadgeColor(VulnSeverity.Medium)}>
            {label} {cvss}
          </Badge>
        </TooltipWrapper>
      );
    }
    if (cvss > 0 && cvss < 4) {
      const label = isTruncated ? 'LOW' : 'Low';
      return (
        <TooltipWrapper
          id="low severity"
          label="Low severity vulnerability"
          cvss={cvss}
          hasTooltip={hasTooltip}
        >
          <Badge color={severityToBadgeColor(VulnSeverity.Low)}>
            {label} {cvss}
          </Badge>
        </TooltipWrapper>
      );
    }
  }

  const label = isTruncated ? 'UNKNOWN' : 'Unknown';
  return (
    <TooltipWrapper
      id="unknown severity"
      label="Unknown severity vulnerability"
      cvss={cvss}
      hasTooltip={hasTooltip}
    >
      <Badge color={severityToBadgeColor(VulnSeverity.Unknown)}>
        {label}
      </Badge>
    </TooltipWrapper>
  );
};

export default VulnBadge;
